// src/pages/ForgotPasswordPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { forgotPassword } from "./../../services/passwordServices";
import { getSettings } from "./../../services/settingsServices";
import useScrollToTop from "./../../hooks/useScollToTop";
import { HiOutlineMail, HiOutlineArrowLeft } from "react-icons/hi";

/* ========= Helpers identiques ErrorPage/Login/Register ========= */
function toAbsolute(u = "") {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${window.location.origin}${u}`;
  return u;
}
function pickIndex(n, exclude = -1) {
  if (n <= 1) return 0;
  let idx = Math.floor(Math.random() * n);
  if (idx === exclude) idx = (idx + 1) % n;
  return idx;
}
/* =============================================================== */

export default function ForgotPasswordPage() {
  useScrollToTop();

  const navigate = useNavigate();

  const [bgUrl, setBgUrl] = useState(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Fond dynamique comme Login / Register
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getSettings();
        const s = Array.isArray(data) ? data[0] : data;

        const raw = [s?.photoURL1, s?.photoURL2, s?.photoURL3].filter(
          (u) => typeof u === "string" && u.trim().length > 0
        );
        const unique = Array.from(new Set(raw));
        const candidates = unique.map(toAbsolute);

        if (!mounted) return;

        if (candidates.length > 0) {
          const prev = Number(sessionStorage.getItem("forgotBgIdx"));
          const idx = pickIndex(
            candidates.length,
            Number.isNaN(prev) ? -1 : prev
          );
          sessionStorage.setItem("forgotBgIdx", String(idx));
          setBgUrl(candidates[idx]);
        } else {
          setBgUrl(null);
        }
      } catch (e) {
        console.error("ForgotPasswordPage getSettings:", e);
        if (mounted) setBgUrl(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setErrorMessage("");
    setLoading(true);

    try {
      const result = await forgotPassword(email);

      if (result?.success) {
        setMessage(
          "Si un compte avec cet email existe, un lien de réinitialisation a été envoyé."
        );
        // Redirection douce vers la page de login
        setTimeout(() => navigate("/login"), 5000);
      } else {
        setErrorMessage(
          result?.message ||
            "Impossible d’envoyer le lien pour le moment. Veuillez réessayer plus tard."
        );
      }
    } catch (err) {
      console.error("forgotPassword error:", err);
      setErrorMessage(
        "Une erreur est survenue. Veuillez réessayer dans quelques instants."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Fond image ou dégradé */}
      <div
        className={`absolute inset-0 ${
          bgUrl
            ? "bg-cover bg-center bg-no-repeat"
            : "bg-gradient-to-b from-slate-900 to-slate-700"
        }`}
        style={bgUrl ? { backgroundImage: `url(${bgUrl})` } : undefined}
        aria-hidden="true"
      />
      {/* Voile */}
      <div
        className="absolute inset-0 bg-slate-900/50 dark:bg-slate-950/60"
        aria-hidden="true"
      />

      {/* Contenu */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-4 text-center text-slate-100">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 text-xs uppercase tracking-wide border border-white/20 mb-3">
              Sécurité du compte
            </span>
            <h1 className="text-3xl font-bold drop-shadow-sm">
              Réinitialisation du mot de passe
            </h1>
            <p className="mt-1 text-sm text-slate-200/90">
              Indiquez votre adresse email pour recevoir un lien de
              réinitialisation.
            </p>
          </div>

          {/* Carte */}
          <div className="bg-white/80 dark:bg-slate-900/90 backdrop-blur-md rounded-2xl shadow-xl border border-white/40 dark:border-slate-800/70 p-6 md:p-7 space-y-4">
            {/* Messages */}
            {message && (
              <div
                className="rounded-xl border border-emerald-200 bg-emerald-50/90 text-emerald-800 text-sm px-3 py-2 flex items-start gap-2 dark:bg-emerald-900/40 dark:border-emerald-800 dark:text-emerald-100"
                role="alert"
              >
                <span className="mt-0.5">✅</span>
                <span>
                  {message}
                  <br />
                  <span className="text-xs opacity-80">
                    Redirection vers la page de connexion dans quelques
                    secondes…
                  </span>
                </span>
              </div>
            )}

            {errorMessage && (
              <div
                className="rounded-xl border border-red-200 bg-red-50/90 text-red-800 text-sm px-3 py-2 flex items-start gap-2 dark:bg-red-900/40 dark:border-red-800 dark:text-red-100"
                role="alert"
              >
                <span className="mt-0.5">⚠️</span>
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Email
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-slate-400">
                    <HiOutlineMail className="h-5 w-5" />
                  </span>
                  <input
                    type="email"
                    className="mt-0 block w-full pl-10 pr-3 py-2.5 bg-white/90 border border-slate-300 rounded-xl text-sm shadow-sm placeholder-slate-400
                               focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/60
                               dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                    placeholder="votre.email@mairie.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* Bouton submit */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full px-4 py-2.5 rounded-xl shadow-sm border text-sm font-medium flex items-center justify-center gap-2
                  transition-transform duration-150
                  ${
                    loading
                      ? "bg-slate-400 text-white cursor-not-allowed border-slate-300"
                      : "bg-primary text-white border-primary hover:bg-white hover:text-primary transform hover:-translate-y-0.5 hover:shadow-md"
                  }`}
              >
                {loading && (
                  <span className="inline-flex h-4 w-4 rounded-full border-2 border-white/60 border-t-transparent animate-spin" />
                )}
                <span>Envoyer le lien de réinitialisation</span>
              </button>

              {/* Aide spams */}
              <div className="rounded-xl border border-sky-100 bg-sky-50/90 px-3 py-2 text-xs text-sky-800 dark:bg-sky-900/40 dark:border-sky-800 dark:text-sky-100">
                Pensez à vérifier également vos courriers indésirables ou
                dossiers « spams » si vous ne voyez pas l’email dans votre
                boîte de réception.
              </div>

              {/* Lien retour */}
              <div className="flex items-center justify-center gap-2 pt-1 text-xs text-slate-500 dark:text-slate-400">
                <HiOutlineArrowLeft className="h-4 w-4" />
                <Link
                  to="/login"
                  className="underline-offset-2 hover:underline font-medium"
                >
                  Retour à la page de connexion
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
